import { Application, Assets, Rectangle, type Texture } from 'pixi.js';
import { el, clear, toast, notifyOnce } from '../dom';
import { clockApi, faunaApi, fieldApi, woodcuttingApi, miningApi, gatheringApi, fishingApi, companionsApi } from '../game-api';
import { settlePickup, pickupFailureMessage } from '../map/drops-pickup';
import { ApiError } from '../../core/network/api';
import { dispatchAction } from '../action/dispatch';
import { createCompanion } from '../map/companion';
import { dropsApi, mobsApi, horseApi, type GroundDrop } from '../game-api';
import type { GameCtx, ViewTeardown } from '../types';
import { generateWorld, generateWorldFromDraft, TILE, BIOME_VOLCANO, type Decor } from '../map/world';
import { createFog } from '../map/fog';
import { initAudio, playSfx, playSfxAt, setListener, setWorldPhase } from '../audio/audio';
import { createMinimap } from './farm/minimap';
import { createAfkGuard, type AfkGuard } from './farm/afk';
import { renderDungeon } from './dungeon';
import { loadMapTextures, mapTextureUrls, RING_FRAMES } from '../map/textures';
import { FOOT_OFFSET } from '../map/player';
import { respawnTile } from '../map/respawn';
import { saveLastPosition, clearLastPosition } from '../map/last-position';
import { saveHorseState } from '../map/horse-state';
import { createJoystick, isTouchDevice } from '../map/joystick';
import { isPhoneLayout } from '../mobile/layout';
import { showFarmAwards } from '../award-popup';
import { createDayNight } from '../map/daynight';
import { animVisionPx, withinAnimVision } from '../map/decor-anim';
import {
  AXE_CURSOR, RUN_TICK_S, TREE_GROW_MS, clampPlayer, closePlanter, createFarmCtx, decorScale,
  decorTexture, hydrateHarvests, loadDraft, refreshBag, treeStageTexture, setEnergyStatus } from './farm/context';
import { destroyBuildGhost } from './farm/build-ghost';
import { tentApi } from './farm/building-menu';
import { isInsideSafeZone } from './farm/placement';
import { buildCollision, buildScene, cullTall, unstickPlayer, cullSmall, cullTerrain, isBlockedPx, isWaterPx, navGrid, sweepRegrowth, updateOcclusion, walkTo } from './farm/scene';
import { worldTimeAt } from '../map/daynight';
import { LIGHT_BUILDINGS, LIGHT_RADIUS_TILES } from '../map/lights';
import type { MobEntity } from '../map/mobs';
import { paintPlots, actOnPlot, resetBuildTotals } from './farm/plots';
import { closeRushPanel } from './farm/construction';
import { closeDemolishConfirm } from './farm/demolish';
import { autoCombatTick, combatTargetTick, breakRock, cancelCut, clearStump, fellTree, gatherProp, mineBoulderPass, settlePendingAction, spawnChips } from './farm/resources';
import { nearObject, nearTile, onHover, onKeyDown, onKeyUp, onPaintDown, onPaintMove, onPaintUp, onTap, onWindowBlur, primaryAction, pumpBuildQueue, buildQueueLength, pumpPendingPlant, pumpTillQueue } from './farm/input';
import { paintChrome, paintHotbar, fmtNextCharge } from './farm/hud';
import { createHpBar } from './farm/hp-bar';
import { createPoisonEffect } from './farm/food';
import { openOracleDialog } from './farm/oracle-dialog';
import { openFishermanDialog } from './farm/fisherman-dialog';
import { createGraveTracker } from './farm/graves';
import { createFarmLoader } from './farm/loading';
import { createDropRegistry } from './farm/drop-registry';
import { gicon } from '../ui/icons';
import { tutorialSeen, tutorialStage, setTutorialStage } from '../tutorial/state';
import { startFarmTour } from '../tutorial/farm-tour';
import { startSandboxTour } from '../tutorial/sandbox-tour';
import { buildSandboxWorld, type SandboxTargets } from '../tutorial/sandbox-world';
import type { CoachHandle } from '../tutorial/coach';

// Animations (coastline waves, campfire fire) only advance within this many tiles
// of the hero — roughly the fog's visible radius. Off-vision props stay frozen so
// we don't burn CPU swapping textures on things the player can't see.
const VISION_TILES = 12;

export function renderFarm(root: HTMLElement, ctx: GameCtx): ViewTeardown {
  clear(root);
  // Browsers block audio until the player interacts; this arms it on the first
  // gesture and starts whatever music was requested in the meantime.
  initAudio();

  // ─── Full-bleed layout: canvas fills the view, HUD floats over it ───────────
  const wrap = el('div', 'relative w-full h-full overflow-hidden');
  const stageHost = el('div', 'absolute inset-0 select-none', { style: 'background:#3f7fb8;touch-action:none' });
  // Vitals column (top-left): HP readout on top, Energy under it. Both are now
  // BOX-LESS — bare bars painted on the world instead of two stacked cards, which
  // on a landscape phone were spending a quarter of the screen on two numbers.
  // `tb-hud-legible` is what keeps them readable without a panel behind them.
  // See design/gdd/hud-bars.md.
  const energyBar = el('div', 'game-font tb-hud-legible w-full');
  const hpBar = createHpBar(wrap);
  const vitals = el('div', 'tb-hud-vitals absolute top-2 left-3 z-20 w-[168px] max-w-[40vw] flex flex-col gap-1', {}, hpBar.el, energyBar);
  const footer = el('div', 'tb-hud-actions game-font absolute top-3 right-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-surface/90 border border-border shadow-lift backdrop-blur-sm flex-wrap justify-end max-w-[58vw]');
  // ─── One bottom BAND, not four independent corners ──────────────────────────
  // The thumbstick, the hotbar and the action button used to be positioned from
  // three different corners with hard-coded offsets, so on a landscape phone the
  // hotbar was clipped by the bottom edge AND overlapped the stick. They are now
  // laid out as one flex row that owns the bottom strip: [stick] [hotbar] [action].
  // The side zones collapse to zero width on desktop (see .tb-band-* in app.css),
  // which puts the hotbar back in the true centre there.
  const hotbar = el('div', 'game-font tb-hotbar flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-surface/80 border border-border shadow-lift backdrop-blur-sm max-w-full overflow-x-auto pointer-events-auto');
  const joyZone = el('div', 'tb-band-joy flex-none flex items-end justify-start pointer-events-auto');
  const actZone = el('div', 'tb-band-act flex-none flex items-end justify-end pointer-events-auto');
  const bottomBand = el('div', 'tb-bottom-band absolute left-0 right-0 bottom-0 z-30 flex items-end gap-2 px-2 pointer-events-none', {},
    joyZone, el('div', 'flex-1 min-w-0 flex justify-center', {}, hotbar), actZone);
  // ─── Right-hand THUMB RAIL ───────────────────────────────────────────────────
  // A column that stacks directly above the action button, on the same right edge:
  // [Build/Village ⋯] · [map toggle] · [action]. The owner's report was that the map
  // toggle was "far from the frame" — it was 187px (landscape) / 610px (portrait)
  // from the action button, parked mid-play-area because it was glued to the map
  // PANEL. The rail is the one place a right thumb can reach without leaving the
  // action button, so everything the thumb needs lives in it and nothing else does.
  // It is absolutely positioned rather than a fourth zone in the band so the band's
  // height (and therefore the play area) does not change; app.css owns the offsets
  // (`--tb-rail-*`) so the stack is declared once for both orientations.
  const thumbRail = el('div', 'tb-thumb-rail absolute z-30 flex flex-col items-end gap-2 pointer-events-auto');

  const c = createFarmCtx(ctx, { wrap, stageHost, energyBar, footer, hotbar });
  /** Seconds since the hero's position was last checkpointed to localStorage. */
  let posSaveAcc = 0;
  // A producer just made these on the server — show them at once rather than
  // waiting for the next /drops poll.
  // `sync` destroys everything NOT in the list it gets, so it must always be handed
  // the whole live set. It used to be handed `[...knownDrops, ...list]` where
  // `knownDrops` was declared and never assigned — permanently `[]` — so each new
  // batch wiped every uncollected drop off the ground (harvest a plot, click the
  // next one, the first harvest was gone). The registry accumulates instead.
  const dropReg = createDropRegistry();
  c.onDrops = (list: GroundDrop[]) => c.drops?.sync(dropReg.add(list, performance.now()));
  /** Repaint the ground from the registry — buildScene() makes a fresh, empty Drops. */
  const resyncDrops = () => c.drops?.sync(dropReg.live(performance.now()));
  /**
   * Ask the server what is still on the ground. Without this a reload showed NO
   * drops at all (dropsApi.list had zero call sites) even though the server still
   * held them for the rest of their TTL.
   */
  const hydrateDrops = () => void dropsApi.list()
    .then(({ drops }) => { if (c.alive) c.onDrops?.(drops); })
    .catch(() => undefined);   // a failed hydrate must never break the farm

  // Block 6: clicking a dungeon entrance drops into the dark cave in a fullscreen
  // overlay (like the arena). The farm's ticker is paused underneath and the whole
  // farm state (energy/hp/bag) is reloaded on the way back, since the dungeon
  // spent energy + credited ore server-side.
  let dungeonTeardown: (() => void) | null = null;
  c.onEnterDungeon = (seed: number) => {
    if (dungeonTeardown) return;
    c.app?.stop();
    const layer = el('div', 'fixed inset-0 z-50 bg-black');
    document.body.appendChild(layer);
    const td = renderDungeon(layer, ctx, seed, () => {
      dungeonTeardown?.();
    }, () => refreshBag(c));   // ore/loot banked underground → the hotbar cache is stale
    dungeonTeardown = () => {
      dungeonTeardown = null;
      td(); layer.remove();
      c.app?.start();
      // c.load() may rebuild the scene, which replaces c.drops with an empty one:
      // repaint the ground we still know about, or coming back up from the dungeon
      // looks like everything rotted.
      void c.load().then(() => { resyncDrops(); return c.game.refreshHud(); });
    };
  };
  // The birch-grove oracle: walk up and talk to open its daily gold-errand dialog.
  // A single open dialog at a time; on claim the wallet HUD is refreshed.
  let oracleClose: (() => void) | null = null;
  c.onTalkOracle = (d) => {
    if (oracleClose) return;
    // Fisherman Fin is drawn as an 'oracle' variant 1 so he inherits the
    // walk-up-and-talk pointer, but he is a different person with a different
    // panel — and no errand, because the game has no fishing to send you on.
    if (d?.variant === 1) {
      // Fin is the only rod seller. The status is re-read after a purchase rather
      // than patched locally: the cast path gates on c.fishCfg.hasRod, and a local
      // flag that drifted from the server would let a player cast with no rod (or
      // refuse one he had just bought).
      oracleClose = openFishermanDialog(wrap, () => { oracleClose = null; }, {
        hasRod: c.fishCfg?.hasRod ?? false,
        price: c.fishCfg?.rodPrice ?? 0,
        buy: async () => {
          await dispatchAction('fishing.rod', (id) => fishingApi.buyRod(id));
          c.fishCfg = await fishingApi.get().catch(() => c.fishCfg);
          await c.game.refreshHud();
          refreshBag(c);
          // Say WHERE it went and WHAT to do: the owner bought one and could not
          // find it (it is a bag item, and casting is a tap on the water).
          toast('A rod of your own 🎣 — it is in your bag. Tap the water from the bank to cast.');
        },
      });
      return;
    }
    // The errand EATS a resource stack and pays coins, so both halves of the
    // chrome are stale on claim — the wallet and the hotbar's material counts.
    oracleClose = openOracleDialog(wrap, () => { refreshBag(c); return c.game.refreshHud(); }, () => { oracleClose = null; });
  };

  // Dev convenience: enter a dungeon from the console without hiking to an entrance.
  if (import.meta.env.DEV) (window as unknown as { __enterDungeon?: (s?: number) => void }).__enterDungeon = (s = 12345) => c.onEnterDungeon?.(s);

  // ─── AFK / auto-exit (Block 10) ──────────────────────────────────────────────
  // 10 min without input soft-exits: the ticker pauses (so no energy drains and
  // the 4-hour lockout timer doesn't run while you're away), position + remaining
  // energy are saved, and an away screen waits. Resuming with zero energy sends
  // you home to the tent rather than back into a mob crowd.
  let afk: AfkGuard | null = null;
  let afkOverlay: HTMLElement | null = null;
  const afkExit = () => {
    if (afkOverlay || dungeonTeardown) return;   // already away, or down a dungeon
    c.app?.stop();
    // 10 minutes idle → SIGN OUT (session safety), not just a pause. Show a brief
    // notice, then hand off to the shell's logout.
    afkOverlay = el('div', 'absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-black/90', {},
      el('div', 'text-3xl font-heading text-white/70 tracking-widest', {}, 'Signed out'),
      el('div', 'text-white/80 text-sm', {}, 'Logged out after 10 minutes of inactivity.'));
    wrap.appendChild(afkOverlay);
    window.setTimeout(() => { c.game.logout?.(); }, 1200);
  };
  afk = createAfkGuard({ host: wrap, idleMs: 10 * 60_000, warnMs: 30_000, onExit: afkExit });

  // ─── Night mobs: dark check, spawning, and the combat callbacks ─────────────
  const worldPhaseIsDark = (fc: typeof c): boolean =>
    sandboxMode ? false : (fc.clockCfg ? worldTimeAt(fc.clockCfg).isDark : false);   // tutorial glade is always day

  function spawnNightWave(fc: typeof c, px: number, py: number, volcano: boolean) {
    if (!fc.mobs || !fc.world) return;
    // Weight the roster: commons often, the chief rarely. The server already
    // filtered to what this player may face, so anything here is fair game.
    // Biome-gated: in the scorched region you meet volcano mobs; elsewhere, meadow.
    const roster = fc.mobStats.filter((m) => (m.biome ?? 'meadow') === (volcano ? 'volcano' : 'meadow'));
    if (!roster.length) return;
    const n = Math.max(1, Math.round(fc.mobSpawnRate / 3));
    for (let i = 0; i < n; i++) {
      const stat = roster[Math.floor(Math.random() * roster.length)];
      if (!stat) continue;
      // Lit zones suppress spawns. The built campfire's light keeps the night
      // away within litZoneTiles — the same suppression the tent gives.
      const lit: { x: number; y: number }[] = [];
      if (fc.campfire) lit.push({ x: fc.campfire.tx, y: fc.campfire.ty });
      // Built torches / lanterns hold the night off too — that is the point of
      // planting them ("protect your crops at night", field.config §8).
      for (const b of fc.farm?.buildings ?? [])
        // …once it is FINISHED: scaffolding doesn't burn.
        if (LIGHT_BUILDINGS[b.kind] && b.isReady !== false) lit.push({ x: b.tx, y: b.ty });
      fc.mobs.spawn(stat, 1, px, py, lit, navGrid(fc));
    }
  }

  // A mob's attack landed. Damage is client-reported; the server clamps it.
  c.onMobHit = (dmg: number) => {
    playSfx('player_hurt');
    void dispatchAction('field.damage', (id) => fieldApi.damage(dmg, id), { noDebounce: true })
      .then((h) => {
        hpBar.set(h);                       // HUD HP bar reacts on the same frame as the hit
        if (h.dead) {
          playSfx('player_death'); toast('You died', 'error');
          graves.refresh();                  // a stone now holds your dropped coins/resources
          // Forget where he fell: death respawns at home/tent, and resuming at the
          // spot that killed him on the next tab switch would be the opposite of
          // what the respawn just decided.
          clearLastPosition();
          // Respawn WHERE THE SERVER SAYS (home → tent → origin). The death
          // animation used to fade the hero out and blink him back in place, so
          // the HP bar's "respawning at your tent" was a promise nothing kept.
          // The move happens after the blink resolves, i.e. while he is invisible.
          const at = h.respawnAt;
          void c.player?.playDeath().then(() => {
            if (!c.alive || !at) return;
            const safe = respawnTile(c, at);
            // Feet space: collision probes y - FOOT_OFFSET, so a respawn that
            // omits it drops the hero's feet a third of a tile north of where he
            // looks like he is standing.
            c.player?.setPos((safe.tx + 0.5) * TILE, (safe.ty + 0.5) * TILE + FOOT_OFFSET);
            // The respawn tile can be solid — a hut is a 3x1 obstacle and the
            // server answers with the tile by its door, which a fence or a
            // regrown tree may since have taken. Without this the hero came back
            // wedged inside geometry and could not move at all.
            unstickPlayer(c);
            c.cam?.centerOn(c.player?.x ?? (safe.tx + 0.5) * TILE, c.player?.y ?? (safe.ty + 0.5) * TILE);
          });
        }
        void c.game.refreshHud();
      })
      .catch(() => undefined);
  };

  // A mob died: report the kill so the server rolls + drops the loot.
  c.onMobDeath = (m: MobEntity) => {
    const tx = Math.floor(m.x / 48), ty = Math.floor(m.y / 48);
    void dispatchAction('mob.kill', (id) => mobsApi.kill(m.stat.id, { tx, ty }, id), { key: m.id })
      .then((r) => {
        c.onDrops?.(r.dropped);
        if (r.leveledUp) playSfx('level_up');
        if (r.dropped.length) toast(`${r.dropped.map((d) => `${d.quantity} ${d.name}`).join(' · ')}${r.leveledUp ? ' · LEVEL UP!' : ''}`);
        void c.game.refreshHud();
      })
      .catch(() => undefined);
  };

  // The chief calls adds at half HP — spawn a couple of orcs beside it.
  c.onMobCallAdds = (m: MobEntity, count: number) => {
    if (!c.mobs) return;
    const orc = c.mobStats.find((s2) => s2.id === 'orc');
    if (orc) c.mobs.spawn(orc, count, m.x, m.y, [], navGrid(c));
    toast('The chief calls for help!', 'error');
  };

  // Food poisoning: a countdown badge, ticking the server's per-second damage
  // locally and reporting each whole point through the same damage path mobs use.
  const poisonBadge = el('div', 'absolute top-16 left-3 hidden px-2 py-1 rounded-md bg-error/85 text-white text-[11px] font-mono pointer-events-none', {}, '');
  const poison = createPoisonEffect((hp) => {
    void dispatchAction('field.damage', (id) => fieldApi.damage(hp, id), { noDebounce: true })
      .then((h) => { hpBar.set(h); if (h.dead) { playSfx('player_death'); toast('The poison took you', 'error'); graves.refresh(); void c.player?.playDeath(); } void c.game.refreshHud(); })
      .catch(() => undefined);
  });
  c.onPoison = (p) => poison.apply(p);

  const hint = el('div', 'absolute bottom-3 left-3 text-[11px] text-white/70 pointer-events-none', {}, 'WASD / click to move · scroll to zoom');
  const zoomBtn = (label: string, f: number) =>
    el('button', 'w-9 h-9 flex items-center justify-center rounded-md bg-black/45 text-white text-xl leading-none hover:bg-black/65', { onClick: () => c.cam?.zoomBy(f) }, label);
  // Zoom + hint live ABOVE the band on desktop; on a phone pinch replaces zoom and
  // the band owns the bottom strip entirely, so both are hidden (see below).
  const zoomCtl = el('div', 'absolute bottom-20 right-3 flex flex-col gap-1 z-20', {}, zoomBtn('+', 1.3), zoomBtn('−', 1 / 1.3));
  const loadingOverlay = el('div', 'absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#1a120d] transition-opacity duration-500', { id: 'farm-loading' },
    el('div', 'w-12 h-12 border-4 border-[#5a4632] border-t-[#ffd97a] rounded-full animate-spin mb-4'),
    el('div', 'game-font text-[#ffd97a] text-sm', {}, 'Entering the world…'));
  wrap.append(stageHost, vitals, footer, hint, zoomCtl, bottomBand, thumbRail, poisonBadge, loadingOverlay);
  // Unclaimed gravestones: the recovery UI for what a death took off you. Polled
  // on load and re-read the moment you die, which is the only time one appears.
  const graves = createGraveTracker(wrap, c);
  graves.refresh();
  // Compact = phone-shaped viewport, touch or not. Hiding these on width/height
  // rather than on `isTouchDevice()` is what makes a narrow desktop window an
  // honest reproduction of the phone layout instead of a different one.
  if (isPhoneLayout()) { hint.style.display = 'none'; zoomCtl.style.display = 'none'; }
  root.append(wrap);

  // The blue rectangle players reported as a bug is now the backdrop of a card that
  // says what is happening and how far along it is. Mounted here, before the first
  // await, so it owns the screen from the first frame — and it stays hidden for
  // 150 ms, so a warm re-entry finishes underneath it and never flashes.
  const loader = createFarmLoader(wrap);

  // ─── Award popup ─────────────────────────────────────────────────────────────
  // Awards are unlocked SERVER-side by `GET /achievements` itself (the service runs
  // its check pass before answering), and nothing else tells the client one was
  // won. So arriving on the farm is what mints and reveals awards earned in the
  // arena, the dungeon, or while logged out — see engine/award-popup.ts for the
  // acknowledge/queue rules. No teardown wiring is needed: the card is a child of
  // `wrap` (removed with the view) and it owns no timers.
  void showFarmAwards(wrap, ctx.user.id);

  /**
   * Put the bought companions in the world.
   *
   * Rebuilt rather than patched whenever ownership changes: there are at most two
   * of them and the alternative is a diffing routine nobody will maintain for a
   * list that long.
   */
  async function spawnCompanions(ctx: typeof c): Promise<void> {
    for (const comp of ctx.companions) comp.destroy();
    ctx.companions = [];
    const st = ctx.companionCfg;
    if (!st || !ctx.entityLayer) return;
    for (const kind of st.owned) {
      const def = st.catalog.find((d: { id: string }) => d.id === kind);
      if (!def) continue;
      try {
        const comp = await createCompanion({
          kind, damage: def.damage, attackEveryMs: def.attackEveryMs,
          engageTiles: def.engageTiles, leashTiles: def.leashTiles,
        }, ctx.player?.x ?? 0, (ctx.player?.y ?? 0) - FOOT_OFFSET);
        // The view may have been torn down while the textures loaded.
        if (!ctx.alive || !ctx.entityLayer) { comp.destroy(); return; }
        ctx.entityLayer.addChild(comp.container);
        ctx.companions.push(comp);
      } catch (e) {
        // A missing frame must not take the farm down with it — but it must not be
        // silent either: a 15 000-Shroom purchase that draws nothing is the single
        // worst way this feature can fail, and the first report of it was "I've
        // bought a demon but it doesn't appear".
        console.warn(`[companion] ${kind} did not spawn:`, e);
        notifyOnce('companion.spawn', `Your ${kind} could not be summoned — reload the farm`, 'error');
      }
    }
  }
  c.respawnCompanions = () => void spawnCompanions(c);

  // ─── Data + actions ──────────────────────────────────────────────────────────
  c.load = async function load() {
    const [farmSnap, cropList, energySnap] = await Promise.all([
      fieldApi.get(),
      c.crops.length ? Promise.resolve(c.crops) : fieldApi.crops(),
      fieldApi.energy(),
    ]);
    c.farm = farmSnap;
    c.crops = cropList;
    // Through the setter, so the rest countdown knows WHEN this was fetched.
    setEnergyStatus(c, energySnap);
    if (!c.cropTex.size && c.crops.length) {
      // One-off: load every plantable's growth stages (files by convention).
      const urls = c.crops.flatMap((crop) => [0, 1, 2, 3].map((st) => `/sprites/crops/${crop.id}_${st}.png`));
      const loaded = (await Assets.load(urls)) as Record<string, Texture>;
      for (const crop of c.crops) c.cropTex.set(crop.id, [0, 1, 2, 3].map((st) => loaded[`/sprites/crops/${crop.id}_${st}.png`]));
    }
    c.fetchedAt = Date.now();
    paintChrome(c);
    // Refresh the hotbar's item preview in the background (non-blocking).
    void fieldApi.bag().then((b) => { c.bagCache = b; paintHotbar(c); }).catch(() => {});
    if (!c.world || c.worldForMax !== (sandboxMode ? -999 : c.farm.maxPlots)) {
      if (sandboxMode) {
        // Phase 1: the calm controls glade — a hand-crafted little map, not the farm.
        const sb = buildSandboxWorld();
        c.world = sb.world;
        sandboxTargets = sb.targets;
        c.worldForMax = -999;
        lightDecor = [];
        c.sandbox = true;   // glade props are props: they must not write farm state
      } else {
        loader.stage('map');
        const d = await loadDraft(c);
        loader.stage('map', 1);
        // World generation is ~310k tiles of SYNCHRONOUS work, so it cannot report
        // sub-progress. Yield a frame first or the browser never paints the label we
        // just set and the bar freezes mid-stage — the exact "frozen" feel this card
        // exists to remove.
        loader.stage('world');
        await new Promise((r) => requestAnimationFrame(r));
        c.world = d
          ? generateWorldFromDraft(c.seed, c.farm.maxPlots, d.data, d.w, d.h)
          : generateWorld(c.seed, c.farm.maxPlots);
        c.worldForMax = c.farm.maxPlots;
        hydrateHarvests(c);   // re-apply server-persisted felled/mined/gathered state
        lightDecor = c.world.decor.filter((d) => d.kind === 'lantern').map((d) => ({ tx: d.tx, ty: d.ty }));
      }
      loader.stage('world', 0.6);
      await new Promise((r) => requestAnimationFrame(r));   // same reason: let the bar paint
      buildScene(c);
      cullTerrain(c);   // build the initial on-screen terrain chunks before the first frame
      loader.stage('world', 1);
      // buildScene tears down every layer INCLUDING entityLayer, so the companions
      // it held were destroyed with it. Nothing else would ever put them back: the
      // spawn runs once at boot, and a world rebuild happens whenever the plot
      // allowance changes. Re-spawn here or a bought companion silently disappears
      // the first time the farm regenerates.
      c.respawnCompanions?.();
    } else {
      // The world mesh is unchanged, but the SNAPSHOT is not: a build that finished
      // between polls flips `isReady`, and a finished bridge/road only clears its
      // (water) tile inside buildCollision. Without this the deck rendered as done
      // while the grid still blocked it — the hero could not step onto the bridge he
      // just paid for until some unrelated placement rebuilt the grid.
      buildCollision(c);
      paintPlots(c);
    }
  };

  // Tutorial phase 1 → 2: leave the glade, regenerate the REAL world with a fresh
  // hero at the farm spawn, then continue the on-map tour. Fail-safe: any error
  // just marks the tutorial done rather than wedging the farm.
  async function enterRealWorld() {
    try {
      setTutorialStage('tour');
      sandboxMode = false;
      c.sandbox = false;
      // Belt and braces for anyone who already has a glade checkpoint saved from a
      // build before the guard above: entering the real world starts at the farm.
      clearLastPosition();
      c.player?.destroy(); c.player = null;   // respawn at the real farm on reload
      c.worldForMax = -1;                     // force the world to regenerate
      await c.load();
      resyncDrops();
      await ctx.refreshHud().catch(() => undefined);
      if (c.alive) tour = startFarmTour(c, wrap);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[tutorial] transition failed:', e);
      setTutorialStage('done');
    }
  }

  // ─── Init ────────────────────────────────────────────────────────────────────
  let lightDecor: { tx: number; ty: number }[] = [];   // lantern/torch decor that glow at night
  let stepAcc = 0;                                      // seconds since the last footstep cue
  let ambAcc = 0;                                       // seconds since the last campfire crackle
  let energyRouted = false;                             // auto-walked home this depletion
  let tour: CoachHandle | null = null;                 // first-run tutorial coach (A4)
  let sandboxMode = tutorialStage() === 'sandbox';     // phase 1: the controls glade
  let sandboxTargets: SandboxTargets | null = null;
  const onToolsChanged = () => paintHotbar(c);
  const onResize = () => {
    // A DESTROYED app is not a null app.
    //
    // `c.app` stays set after the view is torn down, and Pixi's `screen` getter
    // reads through `renderer`, which destroy() clears — so touching it throws
    // "Cannot read properties of undefined (reading 'screen')". A ResizeObserver
    // callback that was already queued still fires after disconnect(), so leaving
    // the farm while anything is resizing (opening settings, a download dialog,
    // rotating the phone) landed exactly in that window. `c.alive` and the renderer
    // check are what make a late callback a no-op instead of an uncaught error.
    if (!c.alive || !c.app?.renderer) return;
    const w = wrap.clientWidth || 820, h = wrap.clientHeight || 520;
    if (c.app.screen.width === w && c.app.screen.height === h) return;
    c.app.renderer.resize(w, h);
    c.cam?.clamp();
    c.dayNight?.resize(c.app.screen.width, c.app.screen.height); // re-bake the vignette at the new size
  };
  // The canvas is sized from `wrap`, and `wrap` changes size for reasons the window
  // never hears about: entering/leaving immersive mode removes the header + tab nav,
  // which grows the view by ~90px with no `resize` event. The renderer kept its old
  // height and the stage's clear colour (#3f7fb8) showed through as a blue strip
  // along the bottom. A ResizeObserver on the wrapper is the honest trigger — the
  // window listener stays for browser-chrome/zoom changes.
  const boxObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => onResize()) : null;
  boxObserver?.observe(wrap);
  const keyDown = onKeyDown(c);
  const keyUp = onKeyUp(c);
  const winBlur = onWindowBlur(c);   // losing focus disarms the Alt clear-modifier
  (async () => {
    // Pre-warm the sprite bundle ourselves so Pixi's per-asset progress can drive
    // the bar; `loadMapTextures` then resolves out of the (global) Assets cache and
    // only does the cheap texture-map assembly. On a warm cache the callback jumps
    // straight to 1, which is honest — that work really is already done.
    loader.stage('sprites');
    await Assets.load(Object.values(mapTextureUrls()), (p) => loader.stage('sprites', p)).catch(() => {});
    c.tex = await loadMapTextures();
    await Assets.load('/sprites/ui/tree_select.png').catch(() => {});  // tree hover bracket
    if (!c.alive) return;
    loader.stage('renderer');
    const app = new Application();
    c.app = app;
    await app.init({ width: wrap.clientWidth || 820, height: wrap.clientHeight || 520, background: '#3f7fb8', antialias: false, preference: 'webgl' });
    if (!c.alive) { app.destroy(); return; }
    stageHost.appendChild(app.canvas);
    // Lighting sits on the STAGE, not in cam.world: it is a screen-space vignette
    // centred on the hero, so panning and zooming must not drag or scale it.
    c.dayNight = createDayNight(app.screen.width, app.screen.height);
    app.stage.addChild(c.dayNight.layer);
    // Tile-based fog of war (world space — pans/zooms with the map, snapped to tiles).
    // Seeded so explored memory persists per world across reloads.
    //
    // Always on: fog is a game rule, not a preference — the player cannot switch off
    // the exploration the map is built around, so there is no setting to consult.
    c.fog = createFog(c.seed);
    // Minimap (explored-terrain overview), mounted in the view's DOM shell.
    c.minimap = createMinimap(c);
    wrap.appendChild(c.minimap.el);
    // Panel and toggle are deliberately in different places: the button joins the
    // thumb rail, the panel keeps the empty strip it needs (see minimap.ts).
    thumbRail.appendChild(c.minimap.toggleEl);
    void clockApi.now().then((clock) => {
      c.clockCfg = {
        epochMs: clock.epochMs, dayMs: clock.dayMs, phases: clock.phases,
        // Trust the server's clock, not the device's.
        offsetMs: clock.serverNow - Date.now(),
      };
    }).catch(() => undefined);   // no clock → no lighting, rather than no game
    // Touch gets a thumbstick: tap-to-walk can't express "hold this direction".
    // It reports through the same setKeys path the keyboard uses.
    if (isTouchDevice()) {
      c.joystick = createJoystick(joyZone, (vx, vy) => {
        cancelCut(c);
        c.player?.setKeys(vx, vy);
      }, { docked: true });
      // Mobile action button (bottom-right, opposite the joystick): the context
      // action on the tile the hero faces — chop / mine / gather / attack / till, or
      // mount the horse. Sits above the safe-area inset on a notched phone.
      // Action button — the right end of the bottom band, so it can never land on
      // the hotbar or the minimap toggle the way a corner-anchored one did.
      const actionBtn = el('button',
        'flex-none z-30 rounded-full bg-purple/70 border-2 border-white/80 text-white text-3xl active:bg-purple flex items-center justify-center select-none',
        { style: 'width:72px;height:72px;touch-action:none' },
        gicon('swords', 30));
      actionBtn.addEventListener('pointerdown', (ev) => { ev.preventDefault(); ev.stopPropagation(); primaryAction(c); });
      actZone.appendChild(actionBtn);
      c.actionBtn = actionBtn;
      // On touch the joystick + pinch replace the desktop hint and zoom buttons —
      // hide them so they don't sit under the joystick / action button.
      hint.style.display = 'none';
      zoomCtl.style.display = 'none';
    }
    app.stage.on('pointertap', onTap(c));
    app.stage.on('pointermove', onHover(c));               // tree hover: axe cursor + selector
    // TOUCH has no hover: a finger produces pointerdown → (maybe move) → up, so
    // without this the build ghost would only ever appear after the tap that
    // already placed the building. Running the same hover pass on pointerdown puts
    // the ghost under the finger the moment it lands, it then follows the drag, and
    // the placement happens on lift (pointertap) — which is the mobile equivalent
    // of "see it before you commit".
    app.stage.on('pointerdown', onHover(c));
    // Drag-to-paint a run of roads/fences/beds. Registered AFTER the camera's own
    // capture-phase listener (attachCamera), so `dragged`/`dragMagnitude` are already
    // reset for this gesture when onPaintDown decides whether it owns it.
    // CAPTURE for the press and GLOBAL for the move, both for the same reason the
    // camera uses capture: sprites that handle their own press (buildings, trees,
    // props) call stopPropagation at target, so a bubble-phase listener here would
    // never see a run that starts on a road tile you already laid — and a stroke
    // that passes over a tree would go dead for the rest of the drag.
    app.stage.on('pointerdowncapture', onPaintDown(c));
    app.stage.on('globalpointermove', onPaintMove(c));
    app.stage.on('pointerupcapture', onPaintUp(c));
    app.stage.on('pointerupoutside', onPaintUp(c));        // finger lifted off the canvas edge
    app.ticker.add((t) => {
      // One frame delta for every system below, clamped: after a tab-switch stall
      // deltaMS can be seconds, which would teleport the hero through walls and
      // fire every timer at once.
      const dt = Math.min(t.deltaMS / 1000, 0.05);
      if (poison.active()) {
        poison.update(dt * 1000);
        poisonBadge.textContent = `Poison ${Math.ceil(poison.remaining())}s`;
        poisonBadge.classList.remove('hidden');
      } else if (!poisonBadge.classList.contains('hidden')) {
        poisonBadge.classList.add('hidden');
      }
      // Coastline waves, torches, the campfire, the furnace AND (since the decor
      // animations were wired up) grass/flowers/mushrooms/the fountain all share
      // this one ticker, and it only animates within the hero's vision — nothing
      // off screen pays per-frame texture swaps. The rule itself lives in
      // map/decor-anim.ts so it can be tested; jsdom cannot drive Pixi.
      if (c.animWater.length) {
        const vp = c.player;
        const visPx = vp ? animVisionPx(VISION_TILES) : Infinity;
        const vx = vp ? vp.x : 0, vy = vp ? vp.y - FOOT_OFFSET : 0;
        for (const a of c.animWater) {
          if (!withinAnimVision(a.sprite.x, a.sprite.y, vx, vy, visPx)) continue;
          a.t += dt;
          if (a.t >= 1 / a.fps) { a.t = 0; a.i = (a.i + 1) % a.frames.length; a.sprite.texture = a.frames[a.i]; }
        }
      }
      if (c.particles.length) {
        c.particles = c.particles.filter((p) => {
          p.life += dt;
          if (p.life >= p.max) { p.s.destroy(); return false; }
          p.vy += 320 * dt;                  // gravity
          p.s.x += p.vx * dt; p.s.y += p.vy * dt; p.s.rotation += p.spin * dt;
          p.s.alpha = 1 - p.life / p.max;
          return true;
        });
      }
      if (c.cutting && c.tex) {               // hold-to-cut: fill the ring, fell at 100%
        c.cutting.t += dt; c.cutting.chipT += dt;
        if (c.cutting.chipT >= 0.3) {
          c.cutting.chipT = 0;
          spawnChips(c, c.cutting.d, c.cutting.sprite);
          // Chopping is HOLD-to-cut, so the tool sound has to tick with the swing
          // rather than fire once at the start — this is the "I don't hear the tree
          // cutting sound" complaint. Chips and sound share one cadence so the
          // impact you hear is the impact you see.
          playSfx(c.cutting.rock ? 'pickaxe_hit' : 'axe_chop');
        }
        const pct = Math.min(1, c.cutting.t / c.cutting.dur);
        c.cutting.ring.texture = c.tex.ring[Math.min(RING_FRAMES - 1, Math.round(pct * (RING_FRAMES - 1)))];
        if (pct >= 1) {
          const { d, sprite, ring, stump, rock, gather } = c.cutting;
          ring.destroy();
          c.cutting = null;
          if (gather) c.player?.stopAction();
          void (d.kind === 'boulder' ? mineBoulderPass(c, d, sprite)
            : gather ? gatherProp(c, d, sprite)
            : rock ? breakRock(c, d, sprite)
            : stump ? clearStump(c, d, sprite)
            : fellTree(c, d, sprite));
        }
      }
      if (c.player) {
        const player = c.player;
        player.update(dt, (x, y) => clampPlayer(c, x, y), (x, y) => isBlockedPx(c, x, y));
        // Everything the world plays is mixed relative to the hero's FEET, so a mob
        // scrapping across the map is quieter than the one biting you.
        setListener(player.x, player.y - FOOT_OFFSET);
        // Footsteps (or hooves) on a cadence tied to the gait — a per-frame cue would
        // be a buzz, and the audio gate would swallow most of it anyway.
        if (player.moving) {
          const mounted = c.riding?.mounted ?? false;
          stepAcc += dt;
          const every = mounted ? 0.42 : player.gait === 'run' ? 0.28 : 0.42;
          if (stepAcc >= every) {
            stepAcc = 0;
            if (mounted) playSfx('horse_gallop');
            else {
              // Tilled soil sounds different from lawn — check the tile under his feet.
              const ftx = Math.floor(player.x / TILE), fty = Math.floor((player.y - FOOT_OFFSET) / TILE);
              const onSoil = !!c.farm?.plots.some((p) => p.tx === ftx && p.ty === fty);
              playSfx(onSoil ? 'footstep_soil' : 'footstep_grass');
            }
          }
        } else stepAcc = 0;
        // Campfire ambience: a crackle every couple of seconds, faded by distance, so
        // the fire is something you can hear your way back to at night.
        if (c.campfire) {
          ambAcc += dt;
          if (ambAcc >= 2.2) {
            ambAcc = 0;
            playSfxAt('campfire', (c.campfire.tx + 0.5) * TILE, (c.campfire.ty + 0.5) * TILE);
          }
        }
        // Reached a queued tree/rock → begin the action; if he stopped short, say so
        // rather than dropping the tap in silence (see settlePendingAction).
        settlePendingAction(c);
        // Reached a queued plot cell → run the farm action; gave up if we stopped short.
        if (c.pendingPlot) {
          if (nearTile(c, c.pendingPlot.tx, c.pendingPlot.ty)) { const pp = c.pendingPlot; c.pendingPlot = null; actOnPlot(c, pp.tx, pp.ty, pp.tool, pp.intent); }
          else if (!player.moving) c.pendingPlot = null;
        }
        // A painted run of beds: the hero walks the row, one bed at a time. Only
        // pumped when nothing else is pending, so a fresh chop/harvest order always
        // wins over the rest of the stroke.
        else if (c.tillQueue.length && !c.pendingAction) pumpTillQueue(c);
        // A painted run of roads/fences. Separate from the bed queue above: placement
        // needs no walking, so it drains on its own pace regardless of what the hero
        // is doing. WITHOUT THIS CALL the queue is filled and never read — a painted
        // run placed nothing at all, which is how it shipped for review.
        if (buildQueueLength()) pumpBuildQueue(c);
        // An acorn tapped onto a distant tile: plant it the moment the hero arrives.
        // Unconditional and on its own line for the same reason as the build queue
        // above — it used to be drained from inside pumpTillQueue, which this loop
        // only calls when a painted BED run is in flight, so a plant walk with an
        // empty tillQueue was queued and never read: the hero walked over and stood
        // there. Cheap when idle (one null check).
        pumpPendingPlant(c);
        // Reached the grove oracle → open its errand dialog; gave up if we stopped short.
        if (c.pendingTalk) {
          if (nearObject(c, c.pendingTalk)) { const pt = c.pendingTalk; c.pendingTalk = null; c.onTalkOracle?.(pt); }
          else if (!player.moving) c.pendingTalk = null;
        }
        // Reached the horse → climb on; gave up if we stopped short.
        // Restoring a ride the tab switch interrupted. The horse was re-placed
        // where it was left, so the hero is standing right by it — one toggle and
        // he is back on it, with no toast: nothing happened from his point of
        // view, which is the entire point.
        if (c.remountPending && c.riding) {
          c.remountPending = false;
          if (c.riding.toggle(player.x, player.y - FOOT_OFFSET) === 'mounted') player.setGait('mount');
        }
        if (c.pendingMount && c.riding) {
          const r = c.riding.toggle(player.x, player.y - FOOT_OFFSET);
          if (r === 'mounted') { c.pendingMount = false; playSfx('horse_mount'); player.setGait('mount'); toast('Mounted 🐴'); }
          else if (!player.moving) c.pendingMount = false;
        }
        // Out of energy → the hero trudges home to sleep it off, once. Reset when
        // energy comes back (slept/rested), so it triggers again next time he's spent.
        //
        // "Home" is his own HUT when one stands (server-resolved, energyStatus.home)
        // and only falls back to the seed-derived farm block otherwise — which is
        // the whole answer to "where is he going": to a building he built, where
        // sleeping actually refills the bar, instead of to a patch of grass.
        if (c.world && (c.energyStatus?.energy ?? c.farm?.energy ?? 1) <= 0) {
          if (!energyRouted) {
            energyRouted = true;
            const bed = c.energyStatus?.home;
            const home = bed ?? c.world.farm;
            if (!walkTo(c, home.tx, home.ty)) player.setTarget((home.tx + 0.5) * TILE, (home.ty + 0.5) * TILE);
            toast(bed ? 'Exhausted — heading home to sleep 💤' : 'Exhausted — build a Hut to sleep it off 💤');
          }
        } else energyRouted = false;
        // Follow the hero only while he's moving; when idle, leave the camera put
        // so you can freely drag around the map.
        // Soft-follow, not a hard centre: the hero may drift inside a 2-tile
        // deadzone before the camera reacts, and what's left is eased.
        if (player.moving) c.cam?.follow(player.x, player.y, dt);
        if (c.fauna || c.livestock) {
          // Animals revive once per world day, so the fauna AI needs the day. Derive
          // it locally from the clock config (same maths daynight uses), 0 if unknown.
          const day = c.clockCfg
            ? Math.floor((Date.now() + c.clockCfg.offsetMs - c.clockCfg.epochMs) / c.clockCfg.dayMs)
            : 0;
          c.fauna?.update(dt, player.x, player.y - FOOT_OFFSET, day, (x, y) => isBlockedPx(c, x, y), (x, y) => isWaterPx(c, x, y));
          // Pen stock runs the same AI on its own instance. Same day/blocked, no
          // water test: livestock is never a water bird. Ticked independently of the
          // wild herd — a map that spawned no animals still has a barn to fill.
          c.livestock?.update(dt, player.x, player.y - FOOT_OFFSET, day, (x, y) => isBlockedPx(c, x, y));
        }
        c.villagers?.update(dt, (x, y) => isBlockedPx(c, x, y));
        if (c.riding) {
          c.riding.update(dt, player.x, player.y - FOOT_OFFSET, (x, y) => isBlockedPx(c, x, y));
          // Mounted → ride at the horse's type speed; dismount restores normal gaits.
          if (c.riding.mounted) { player.setMountSpeed(c.riding.mountSpeed()); player.setGait('mount'); }
          // Seat/unseat the hero from the ONE authoritative flag rather than at each
          // of the six mount/dismount call sites — a missed site is a hero standing
          // upright on a galloping horse, and there is no way to notice that from
          // the code. setRiding() no-ops when already in the right state.
          player.setRiding(c.riding.mounted);
        }
        // Sword equipped → auto-swing at the nearest mob/animal in range (field only).
        autoCombatTick(c, dt * 1000);
        combatTargetTick(c, dt * 1000);   // pursue + finish a clicked mob
        // Night mobs: spawn on a timer while it's dark, update every frame.
        c.effects?.update(dt);   // §7: arrows / slashes / sparks / damage numbers
        c.heldTorch?.update(dt); // flame frames on the carried torch, in step with the hand
        if (c.mobs && c.clockCfg) {
          const isDark = worldPhaseIsDark(c);
          // Standing in your own camp means nothing hunts you — the whole point of
          // the tent. Computed here rather than asked of the server per frame; the
          // server owns the rule and re-states `safeRadius` on every tent call.
          const safe = c.tent?.pitched === true && isInsideSafeZone(
            { tx: c.tent.tx ?? 0, ty: c.tent.ty ?? 0 },
            Math.floor(player.x / TILE), Math.floor((player.y - FOOT_OFFSET) / TILE),
            c.tent.safeRadius,
          );
          c.mobs.update(dt, player.x, player.y - FOOT_OFFSET, isDark, navGrid(c), performance.now(), c.riding?.fearless() ?? false, safe);

          // Bought companions (angel/demon) fight the fight the player is already
          // in: they strike through the SAME mobs.hitAt the sword uses, which fires
          // the death hook that credits the kill and rolls the loot server-side. A
          // companion kill and a player kill are therefore the same event, priced by
          // the same code — no second combat path, and nothing to keep in step.
          if (c.companions.length) {
            const hx = player.x, hy = player.y - FOOT_OFFSET;
            const live = c.mobs.entities;
            for (const comp of c.companions) {
              const hit = comp.update(dt, hx, hy, live, performance.now());
              if (!hit) continue;
              const res = c.mobs.hitAt(hit.target.x, hit.target.y, hit.damage);
              if (res && c.effects) {
                // Its own colour, so a companion's damage number is never mistaken
                // for the player's own hit.
                c.effects.hit(hit.target.x, hit.target.y - TILE * 0.4, hit.damage, 0xb56bff);
              }
            }
          }
          // The volcano is hostile day AND night; the meadow only spawns after dark.
          const inVolcano = !!c.world && c.world.biome?.[
            Math.floor((player.y - FOOT_OFFSET) / 48) * c.world.cols + Math.floor(player.x / 48)
          ] === BIOME_VOLCANO;
          if ((isDark || inVolcano) && c.mobStats.length) {
            c.mobSpawnAccMs += dt * 1000;
            if (c.mobSpawnAccMs >= c.mobTuning.spawnEveryMs) {
              c.mobSpawnAccMs = 0;
              spawnNightWave(c, player.x, player.y - FOOT_OFFSET, inVolcano);
            }
          }
        }
        if (c.drops) {
          const reach = c.drops.update(dt, player.x, player.y - FOOT_OFFSET, performance.now());
          // Walking into a pile puts SEVERAL drops in reach on one frame. Claiming
          // them one-per-request meant a dozen calls in a few milliseconds for a
          // single ordinary pickup — wasteful, and indistinguishable from abuse at
          // the rate limiter. One batched call takes exactly the drops reached.
          if (reach.length) {
            const ids = reach.slice();
            void dispatchAction('drops.claim', (aid) => dropsApi.claimMany(ids, aid), { key: ids[0], noDebounce: true })
              .then((res) => {
                if (res.collected.length) playSfx('pickup_item');   // one sound for the pile, not one per item
                const out = settlePickup(ids, res);
                // Forget them in the REGISTRY too, not only in the Pixi layer: the
                // registry is what the next sync() is built from, so a claimed drop
                // left in it is respawned on the ground and re-claimed forever
                // ('Already picked up' on every frame).
                for (const id of out.remove) { c.drops?.remove(id); dropReg.forget(id); }    // banked, or already gone
                for (const id of out.release) c.drops?.release(id);  // never attempted — retry later
                notifyOnce('drops.full', out.fullMessage, 'error');
                notifyOnce('drops.claim', null);   // the request worked — re-arm the failure toast
                void c.game.refreshHud();
                // ...and the BAG, which is where the picked-up stack actually lands.
                // refreshHud() only re-reads the header wallet (coins/shrooms), so
                // without this the hotbar kept showing the count from before the
                // pickup — "I collected 3 stone and my stack didn't go up".
                refreshBag(c);
              })
              // The whole batch failed (offline, 429). Put them back in play so a
              // later frame retries: dropping them would make a failed request
              // look like a successful pickup, and leaving them flagged `claiming`
              // would make them permanently unpickable.
              .catch((e: unknown) => {
                for (const id of ids) c.drops?.release(id);
                // Nothing to say for our own debounce; everything else (offline,
                // 500, 429) gets a toast — it used to get silence. notifyOnce keeps
                // it to one per failure streak: the same drops are re-reached every
                // frame while the player stands there.
                const msg = pickupFailureMessage(e);
                if (msg) notifyOnce('drops.claim', msg, 'error');
              });
          }
        }
        if (c.dayNight && c.cam) {
          // Hero world → screen, so the light follows him rather than the camera.
          const sc = c.cam.getScale();
          c.dayNight.update(c.clockCfg, player.x * sc + c.cam.world.x, (player.y - FOOT_OFFSET) * sc + c.cam.world.y, false, sc);
          // Warm light pools at every burning source. These are painted at ALL
          // hours (dimmer by day — see DAY_LIGHT_FLOOR): a lit torch that threw no
          // light was the whole "connected without the shine" complaint, and with
          // ALWAYS_DAY on, gating the pools on darkness meant they never showed.
          const dark = worldPhaseIsDark(c);
          const lights: { x: number; y: number; r: number }[] = [];
          const cam = c.cam;
          const toScreen = (tx: number, ty: number, rTiles: number) => ({
            x: (tx + 0.5) * TILE * sc + cam.world.x, y: (ty + 0.5) * TILE * sc + cam.world.y, r: rTiles * TILE * sc,
          });
          if (c.campfire) lights.push(toScreen(c.campfire.tx, c.campfire.ty, LIGHT_RADIUS_TILES.campfire));
          for (const l of lightDecor) lights.push(toScreen(l.tx, l.ty, LIGHT_RADIUS_TILES.lantern));
          // Torches / lanterns the player built.
          // …finished ones only: scaffolding doesn't burn, so it must not clear a
          // hole in the darkness either (same gate the spawn suppression uses).
          for (const b of c.farm?.buildings ?? [])
            if (LIGHT_BUILDINGS[b.kind] && b.isReady !== false) lights.push(toScreen(b.tx, b.ty, LIGHT_RADIUS_TILES[b.kind] ?? 2));
          // The torch in hand travels with the hero (his FEET, so the pool sits on
          // the ground rather than around his head).
          if (c.torchLit) lights.push({
            x: player.x * sc + cam.world.x,
            y: (player.y - FOOT_OFFSET * 0.4) * sc + cam.world.y,
            r: LIGHT_RADIUS_TILES.held_torch * TILE * sc,
          });
          c.dayNight.paintLights(lights, dark ? 1 : 0);
          if (c.dayNight.layer.parent) c.app!.stage.setChildIndex(c.dayNight.layer, c.app!.stage.children.length - 1);
        }
        // Rest-charge countdown, interpolated between server reads the same way the
        // crop timers are: energyStatus carries the ms remaining at the moment it
        // was fetched, so the elapsed time since is subtracted here. Without this
        // the label would freeze at whatever the last poll said, which is the same
        // "number that never moves" that hid the missing regen in the first place.
        if (c.restTimerLabel && c.energyStatus) {
          const en = c.energyStatus;
          const left = en.nextRefillMs === undefined
            ? undefined
            : Math.max(0, en.nextRefillMs - (Date.now() - (c.energyStatusAt ?? Date.now())));
          const text = fmtNextCharge(en.refills, en.maxRefills, left) ?? '';
          // Touch the DOM only when the text actually changes: this runs 60×/s.
          if (c.restTimerLabel.textContent !== text) c.restTimerLabel.textContent = text;
          // The charge has landed — ask the server for the real counts once, rather
          // than inventing a circle the server has not agreed to.
          if (left !== undefined && left <= 0 && en.refills < en.maxRefills) {
            setEnergyStatus(c, { ...en, nextRefillMs: undefined });   // one-shot latch
            void c.game.refreshHud();
          }
        }
        if (c.clockLabel && c.clockCfg) {
          const wt = worldTimeAt(c.clockCfg);
          const mins = Math.floor(wt.dayProgress * 24 * 60);
          const hh = String(Math.floor(mins / 60)).padStart(2, '0');
          const mm = String(mins % 60).padStart(2, '0');
          // The pack has no sun/moon icon, and a system emoji next to pixel art is
          // exactly what the icon pass removed — so the phase is spelled out.
          const phase = wt.phase === 'day' ? 'Day' : wt.phase === 'dusk' ? 'Dusk' : wt.phase === 'dawn' ? 'Dawn' : 'Night';
          c.clockLabel.textContent = `${phase} ${hh}:${mm} · Day ${wt.dayNumber + 1}`;
          // The music bed follows the world clock. Dusk and dawn count as night so
          // the change lands with the light, not an hour after it. The farm reports
          // the PHASE only — whether that phase is audible at all is the view
          // resolver's call, so this can no longer keep the farm bed alive from
          // under another tab (see audio/track.ts).
          setWorldPhase(wt.phase === 'day' ? 'day' : 'night');
        }
        // Tile fog lives in the world so it pans/zooms with the map; keep it topmost,
        // recentre it on the hero's tile, and tell it which tiles are on screen so it
        // only paints those (black over unseen, dim over explored, clear in sight).
        if (c.fog && c.cam && c.app) {
          if (c.fog.layer.parent !== c.cam.world) c.cam.world.addChild(c.fog.layer);
          c.cam.world.setChildIndex(c.fog.layer, c.cam.world.children.length - 1);
          const fsc = c.cam.getScale(), sw = c.app.screen.width, sh = c.app.screen.height;
          c.fog.update(player.x / TILE, player.y / TILE, {
            minTx: Math.floor((-c.cam.world.x) / (TILE * fsc)),
            minTy: Math.floor((-c.cam.world.y) / (TILE * fsc)),
            maxTx: Math.ceil((sw - c.cam.world.x) / (TILE * fsc)),
            maxTy: Math.ceil((sh - c.cam.world.y) / (TILE * fsc)),
          });
        }
        c.minimap?.update(performance.now());
        // An obstacle appeared (or vanished) since this walk was planned — re-plan
        // from where the hero actually is, rather than finish walking into it.
        if (c.walkGoal && c.walkGoal.v !== c.gridVersion) {
          if (player.moving) { const g = c.walkGoal; if (!walkTo(c, g.tx, g.ty)) c.walkGoal = null; }
          else c.walkGoal = null;
        }
        if (!player.moving && c.walkGoal) c.walkGoal = null;
        if (player.gait === 'run' && player.moving) {
          c.runMeter += dt;
          if (c.runMeter >= RUN_TICK_S) {
            c.runMeter = 0;
            void dispatchAction('field.burn', (id) => fieldApi.burn('run', id), { noDebounce: true })
              .then((r) => {
                if (!r.spent && !c.outOfPuff) {   // energy gone — drop back to a walk
                  c.outOfPuff = true;
                  c.player?.setGait('walk');
                  toast('Out of puff — walking', 'error');
                }
                void ctx.refreshHud();
              })
              .catch(() => undefined);         // a dropped/failed tick just means no charge this interval
          }
        } else {
          c.runMeter = 0;
          if (c.outOfPuff && !player.moving) c.outOfPuff = false;   // let them try again once stopped
        }
        // Re-seed anything whose regrow timer elapsed, on a NEW tile, before the
        // cullers rebuild sprites from the (now updated) buckets.
        // Also checkpoint while playing, so a refresh or a crash resumes where the
        // player was rather than at the last clean exit. Throttled: this is a
        // localStorage write, not something to do sixty times a second.
        posSaveAcc += dt;
        // NOT in the tutorial glade. The glade is a different 20x50 map drawn under
        // the same seed, so checkpointing there wrote glade coordinates into the
        // real world's resume point — and the player then "resumed" at whatever the
        // real map has at that tile, which on a good many seeds is the pond. That is
        // the "I appear in the water" report; pickSpawn only covers the case where
        // there is no saved point at all, so the bad point must not be written.
        if (posSaveAcc >= 3 && c.player && !c.sandbox) { posSaveAcc = 0; saveLastPosition(c.seed, c.player.x, c.player.y); }
        sweepRegrowth(c);
        cullTerrain(c);
        cullTall(c);
        cullSmall(c);
        updateOcclusion(c);
        // Regrow felled trees whose timer elapsed (only those currently in view).
        if (c.felledUntil.size && c.tex) {
          const now = Date.now();
          for (const [id, s] of c.activeTall) {
            const until = c.felledUntil.get(id);
            if (until === undefined || now <= until) continue;
            const d = (s as unknown as { decor?: Decor }).decor;
            if (d) {
              // Regrow as a true sapling sprite; it stages up to the full tree
              // (see treeStageTexture) rather than scaling a shrunken adult.
              s.texture = treeStageTexture(c, d, 0);
              s.eventMode = 'static'; s.tint = 0xffffff; s.visible = true; s.scale.set(decorScale(d));
              const tf = s.texture.frame;
              s.hitArea = new Rectangle(-tf.width / 2, -tf.height, tf.width, tf.height * 0.85);
              c.treeGrowStart.set(id, now);
            }
            c.felledUntil.delete(id); c.clearedStumps.delete(id);
          }
        }
        // Advance regrowing trees through their sprite stages (sapling → … → full).
        if (c.treeGrowStart.size && c.tex) {
          const now = Date.now();
          for (const [id, start] of c.treeGrowStart) {
            const gs = c.activeTall.get(id);
            const gd = gs && (gs as unknown as { decor?: Decor }).decor;
            if (!gs || !gd) { c.treeGrowStart.delete(id); continue; }
            const t2 = Math.min(1, (now - start) / TREE_GROW_MS);
            const stage = treeStageTexture(c, gd, t2);
            if (gs.texture !== stage) {
              gs.texture = stage;
              const tf = stage.frame;
              gs.hitArea = new Rectangle(-tf.width / 2, -tf.height, tf.width, tf.height * 0.85);
            }
            if (t2 >= 1) c.treeGrowStart.delete(id);
          }
        }
        // Regrow mined-out ore rocks whose timer elapsed (in-view only).
        if (c.minedUntil.size && c.tex) {
          const now = Date.now();
          for (const [id, s] of c.activeTall) {
            const until = c.minedUntil.get(id);
            if (until === undefined || now <= until) continue;
            const d = (s as unknown as { decor?: Decor }).decor;
            if (d) {
              s.texture = decorTexture(c.tex, d); s.scale.set(decorScale(d));
              s.eventMode = 'static'; s.tint = 0xffffff; s.visible = true; s.cursor = AXE_CURSOR;
              const tf = decorTexture(c.tex, d).frame;
              s.hitArea = new Rectangle(-tf.width / 2, -tf.height, tf.width, tf.height);
            }
            c.minedUntil.delete(id);
          }
        }
        // Respawn gathered rocks/mushrooms whose timer elapsed (in-view only).
        if (c.gatheredUntil.size && c.tex) {
          const now = Date.now();
          for (const [id, s] of c.activeTall) {
            const until = c.gatheredUntil.get(id);
            if (until === undefined || now <= until) continue;
            const d = (s as unknown as { decor?: Decor }).decor;
            if (d) {
              s.texture = decorTexture(c.tex, d); s.scale.set(decorScale(d));
              s.eventMode = 'static'; s.tint = 0xffffff; s.visible = true; s.cursor = 'pointer';
              const tf = decorTexture(c.tex, d).frame;
              s.hitArea = new Rectangle(-tf.width / 2 - 2, -tf.height - 2, tf.width + 4, tf.height + 4);
            }
            c.gatheredUntil.delete(id);
          }
        }
      }
    });
    window.addEventListener('resize', onResize);
    window.addEventListener('tb-chrome-changed', onResize);
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    window.addEventListener('blur', winBlur);
    // The Tools dashboard changed a tool→icon assignment → repaint the hotbar (the
    // hover cursor re-reads the mapping on its own each pointermove). The event name
    // must match what settings-modal.ts dispatches — a listener on a name nobody
    // fires is worse than no listener, because the next reader trusts it.
    window.addEventListener('tb-tool-icons-changed', onToolsChanged);
    // Stats must land BEFORE load(), which builds the scene and spawns the herd —
    // fetched after it and the animals run on defaults for the session. Failing is
    // survivable (see DEFAULT_STATS) but must not be silent: swallowing this is what
    // hid an empty world for a whole session.
    // Four sequential status calls, ticked one by one — the sub-progress is the
    // count of settled requests, not a clock. Their ORDER is load-bearing (see
    // above): do not reshuffle them to make the bar smoother.
    loader.stage('farm');
    const fs = await faunaApi.status().catch((e: unknown) => {
      console.warn('[fauna] stats fetch failed — falling back to defaults', e);
      return null;
    });
    if (fs) {
      c.faunaStats = Object.fromEntries(fs.species.map((sp) => [sp.id, sp]));
      c.faunaTuning = fs.ai;
    }
    // Which mobs this player may face, and how hard — server-gated (level/night).
    loader.stage('farm', 0.25);
    const ms = await mobsApi.status().catch(() => null);
    if (ms) { c.mobStats = ms.mobs; c.mobTuning = ms.ai; c.mobSpawnRate = ms.spawnRate; }
    loader.stage('farm', 0.5);
    c.horseStatus = await horseApi.status().catch(() => null);
    // Where the tent stands, and how big its safe zone is. Fetched once at load and
    // then refreshed by every tent action — the mob loop reads it every frame and
    // cannot afford a request per tick.
    loader.stage('farm', 0.75);
    c.tent = await tentApi.get().catch(() => null);
    // PITCH IT IF IT HAS NEVER BEEN PITCHED.
    //
    // The server cannot do this itself: the world is generated on the CLIENT from a
    // seed, so only we know where this player's farm sits. `POST /field/tent` exists
    // for exactly that handshake and writes only while the tile is unset — and
    // nothing in the client had ever called it. Every account therefore ran with
    // tentTx null: no safe ground anywhere, and death respawning at the world
    // origin instead of at camp. That included the admin account, which is what
    // made it look like a rule the admin was exempt from rather than a rule nobody
    // had.
    //
    // `tx === null` is "never pitched" and is the only case we fix. A tent the
    // player deliberately PACKED (stowed) keeps its tx/ty, so it is left alone —
    // auto-pitching that would undo a choice they made.
    if (c.tent && c.tent.tx === null && c.world?.farm) {
      await fieldApi.tent(c.world.farm.tx, c.world.farm.ty).catch(() => null);
      c.tent = await tentApi.get().catch(() => c.tent);
    }
    // A PACKED tent is a real, chosen state, and worth saying out loud: with
    // nothing pitched there is no ground where mobs leave you alone, and death
    // sends you back to the farm rather than to a camp of your choosing.
    //
    // It used to say "you will respawn at the world origin", which was both true
    // and terrible — the origin is tile (0,0), the top-left corner of the map,
    // open sea on most seeds. That is now routed through pickSpawn (map/respawn.ts),
    // so the sentence would be a lie as well as a threat. `notifyOnce` keys it so
    // it is said once per state rather than on every reload.
    if (c.tent && !c.tent.pitched) {
      notifyOnce('tent', 'Your tent is packed — pitch it somewhere, or you will wake up back at the farm', 'error');
    } else {
      notifyOnce('tent', null);
    }
    await c.load();
    loader.stage('valley');
    hydrateDrops();   // drops the server still holds survive a reload
    c.woodCfg = await woodcuttingApi.status().catch(() => null); // chop-feel constants
    loader.stage('valley', 1 / 7);
    c.mineCfg = await miningApi.status().catch(() => null);      // mine-feel constants
    loader.stage('valley', 2 / 7);
    c.fishCfg = await fishingApi.get().catch(() => null);        // rod ownership + cast cost
    loader.stage('valley', 3 / 7);
    c.companionCfg = await companionsApi.get().catch(() => null);  // bought angel/demon
    loader.stage('valley', 4 / 7);
    await spawnCompanions(c);
    loader.stage('valley', 5 / 7);
    c.gatherCfg = await gatheringApi.status().catch(() => null); // stone/mushroom respawn times
    loader.stage('valley', 6 / 7);
    await ctx.refreshHud();                                     // show wood count
    loader.done();
    // Tutorial (A4): phase 1 = the controls glade, phase 2 = the on-map tour.
    if (c.alive && !tour) {
      if (sandboxMode && sandboxTargets) {
        tour = startSandboxTour(c, wrap, sandboxTargets, () => void enterRealWorld());
      } else if (tutorialStage() === 'tour' && !tutorialSeen()) {
        tour = startFarmTour(c, wrap);
      }
    }
  })().catch((e) => {
    const msg = e instanceof ApiError ? e.message : (e as Error)?.message ?? String(e);
    // The failure lives INSIDE the card now. A bare red line under the blue told the
    // player the game was broken and gave them nothing to do about it; the only
    // recovery any of us ever used was a reload, so that is what Retry does —
    // nothing in GameCtx can re-mount a single view.
    loader.fail(msg, () => window.location.reload());
    // eslint-disable-next-line no-console
    console.error('[farm] init failed:', e);
  });

  const tick = window.setInterval(() => paintPlots(c), 1000);

  return () => {
    c.alive = false;
    // Leaving mid-load: kill the card's pending show/fade timers, or they wake up
    // on a detached node (the ResizeObserver lesson, above).
    loader.destroy();
    graves.destroy();
    destroyBuildGhost(c);
    hpBar.destroy();
    tour?.destroy();
    afk?.destroy();
    afkOverlay?.remove();
    dungeonTeardown?.();
    c.fauna?.destroy();
    c.fauna = null;
    c.livestock?.destroy();
    c.livestock = null;
    c.villagers?.destroy();
    c.villagers = null;
    c.drops?.destroy();
    c.drops = null;
    c.mobs?.destroy();
    c.mobs = null;
    // Where the horse stood, and whether he was on it — read BEFORE the layer is
    // destroyed, because destroying it is what used to lose both. A tab switch runs
    // this teardown, which is why a ride ended at the Shop button (map/horse-state.ts).
    {
      const at = c.riding?.position();
      if (at && !c.sandbox) saveHorseState(c.seed, at.tx, at.ty, !!c.riding?.mounted);
    }
    c.riding?.destroy();
    c.riding = null;
    c.dayNight?.destroy();
    c.dayNight = null;
    c.effects?.destroy();
    c.effects = null;
    // NOT stopAudio(): leaving the farm is a view change, and the shell's tab
    // switcher already re-resolves the bed (silence on a menu tab, the arena bed in
    // a match). Killing every pool here also killed the click that caused the
    // switch. Only sign-out stops everything (bootstrap teardown).
    c.fog?.destroy();
    c.fog = null;
    c.minimap?.destroy();
    c.minimap = null;
    c.joystick?.destroy();
    c.joystick = null;
    c.actionBtn?.remove();
    c.actionBtn = null;
    window.clearInterval(tick);
    boxObserver?.disconnect();
    window.removeEventListener('resize', onResize);
    window.removeEventListener('tb-chrome-changed', onResize);
    window.removeEventListener('keydown', keyDown);
    window.removeEventListener('keyup', keyUp);
    window.removeEventListener('blur', winBlur);
    window.removeEventListener('tb-tool-icons-changed', onToolsChanged);
    // Remember where the hero stood BEFORE anything is destroyed: leaving the farm
    // (a tab switch) is exactly the moment the position used to be thrown away.
    if (c.player && !c.sandbox) saveLastPosition(c.seed, c.player.x, c.player.y);
    closePlanter(c);
    closeRushPanel();          // a `fixed inset-0` modal would outlive the view and eat every click
    // Same reason, worse consequence: a demolish confirm left open after leaving the
    // farm would still fire unbuild + repaint against this torn-down ctx.
    closeDemolishConfirm();
    resetBuildTotals();
    cancelCut(c);
    for (const p of c.particles) p.s.destroy();
    c.particles = [];
    c.heldTorch?.destroy();
    c.heldTorch = null;
    c.player?.destroy();
    c.player = null;
    c.cam?.destroy();
    c.app?.destroy(true);
    c.app = null;
  };
}
